/**
 * Tracer Context
 * Preserves Tracer page state across route navigation
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { PathfinderDataPoint, PathfinderRoute } from '../services/pathfinderApi';
import { DrawCircle } from '../components/Pathfinder/AreaSearch';
import { AnalysisTab } from '../components/Pathfinder/GeoNeutronDataTable';

export interface TracerState {
  dataPoints: PathfinderDataPoint[];
  routes: PathfinderRoute[];
  visibleDataPoints: PathfinderDataPoint[];
  visibleRoutes: PathfinderRoute[];
  timeRange: { start: Date; end: Date } | null;
  currentTime: Date;
  drawCircles: DrawCircle[];
  selectedStudents: string[];
  selectedDevices: Record<string, string[]>;
  actualDevices: Record<string, any[]>;
  dwellLocations: any[];
  visibleDwells: any[];
  analysisTabs: AnalysisTab[];
  mapCenter: [number, number];
  mapZoom: number;
  // Note: enablePlaceNames moved to Zustand store for persistence
}

interface TracerContextType {
  state: TracerState;
  updateState: (updates: Partial<TracerState> | ((prev: TracerState) => Partial<TracerState>)) => void;
  resetState: () => void;
}

const defaultState: TracerState = {
  dataPoints: [],
  routes: [],
  visibleDataPoints: [],
  visibleRoutes: [],
  timeRange: null,
  currentTime: new Date(),
  drawCircles: [],
  selectedStudents: [],
  selectedDevices: {},
  actualDevices: {},
  dwellLocations: [],
  visibleDwells: [],
  analysisTabs: [],
  mapCenter: [39.8283, -98.5795], // Default: center of continental US
  mapZoom: 4, // Default: zoom to show continental US
};

const TracerContext = createContext<TracerContextType | undefined>(undefined);

export const useTracerState = () => {
  const context = useContext(TracerContext);
  if (!context) {
    throw new Error('useTracerState must be used within a TracerProvider');
  }
  return context;
};

interface TracerProviderProps {
  children: ReactNode;
}

export const TracerProvider: React.FC<TracerProviderProps> = ({ children }) => {
  const [state, setState] = useState<TracerState>(defaultState);

  const updateState = (updates: Partial<TracerState> | ((prev: TracerState) => Partial<TracerState>)) => {
    if (typeof updates === 'function') {
      setState(prev => ({ ...prev, ...updates(prev) }));
    } else {
      setState(prev => ({ ...prev, ...updates }));
    }
  };

  const resetState = () => {
    setState(defaultState);
  };

  return (
    <TracerContext.Provider value={{ state, updateState, resetState }}>
      {children}
    </TracerContext.Provider>
  );
};

